import React, { useEffect, useRef } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "videojs-contrib-quality-levels";
import "videojs-hls-quality-selector";
import "./VideoPlayer.css";

/**
 * VideoPlayer - Video.js 기반 교육용 비디오 플레이어
 */
const VideoPlayer = ({
  src,
  subtitles = [],
  onProgress,
  onDuration,
  onEnded,
  onReady,
  onPlay,
  onPause,
  onFullscreenChange,
  startTime = 0,
  autoplay = false,
  volume = 0.8,
  isCompleted = false,
  className = "",
  style = {},
}) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);

  // HLS (.m3u8) 형식인지 확인하는 함수
  const isHlsSource = (source) => {
    if (!source) return false;
    const url = typeof source === "string" ? source : source.src;
    return url && url.toLowerCase().includes(".m3u8");
  };

  useEffect(() => {
    if (!videoRef.current) {
      console.log("[VideoPlayer] No video element");
      return;
    }

    const isHls = isHlsSource(src);
    console.log("[VideoPlayer] Is HLS source:", isHls);

    // Video.js 플레이어 생성
    const player = videojs(videoRef.current, {
      controls: true,
      autoplay: autoplay,
      preload: "auto",
      fluid: true,
      playbackRates: [1], // 1배속만 허용
      html5: {
        nativeTextTracks: false,
        nativeControlsForTouch: false, // 모바일에서도 커스텀 컨트롤 사용
        hls: {
          withCredentials: false,
          enableLowInitialPlaylist: true,
          smoothQualityChange: true,
          overrideNative: true,
        },
      },
      controlBar: {
        playbackRateMenuButton: false, // 배속 버튼 숨김
        remainingTimeDisplay: false, // 남은 시간 숨김
      },
      // 모바일 터치 동작 설정
      userActions: {
        hotkeys: true,
        doubleClick: false, // 더블클릭 전체화면 비활성화
      },
    });

    playerRef.current = player;

    // 소스 설정
    player.src({
      src: src,
      type: isHls ? "application/x-mpegURL" : "video/mp4",
    });

    // 볼륨 설정 - 모바일에서는 최대 볼륨으로 고정
    const isMobile =
      /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
      window.innerWidth <= 768;
    if (isMobile) {
      player.volume(1.0); // 모바일에서는 최대 볼륨으로 고정
      console.log("[VideoPlayer] Volume set to maximum for mobile");
    } else {
      player.volume(volume);
    }

    // 모바일 이벤트 리스너 참조(클린업에서 접근 필요)
    let mobilePlayerEl = null;
    let mobileOnTouchStart = null;
    let mobileOnPointerDown = null;
    let mobileOnClick = null;
    let handleDocumentClick = null;
    let mobileObserver = null;
    let desktopObserver = null;
    let mobileMutationTimeout = null;
    let mutationTimeout = null;

    // 이벤트: 플레이어 준비 완료
    player.ready(() => {
      console.log("[VideoPlayer] Player ready");

      // 완료된 콘텐츠에 CSS 클래스 추가
      if (isCompleted) {
        player.el().classList.add("vjs-completed");
        console.log(
          "[VideoPlayer] Added completed class for seek functionality"
        );
      }

      // HLS 품질 선택 기능 활성화
      if (isHls && player.qualityLevels) {
        console.log("[VideoPlayer] Initializing quality selector for HLS");
        try {
          // qualityLevels API 활성화
          player.qualityLevels();

          // hlsQualitySelector가 있으면 초기화
          if (typeof player.hlsQualitySelector === "function") {
            player.hlsQualitySelector({
              displayCurrentQuality: true,
            });
            console.log("[VideoPlayer] Quality selector initialized");
            // 품질 버튼을 PiP 왼쪽(없으면 Fullscreen 왼쪽)으로 이동
            try {
              const controlBarEl =
                player.controlBar && player.controlBar.el
                  ? player.controlBar.el()
                  : null;
              if (controlBarEl) {
                const qualityBtn = controlBarEl.querySelector(
                  ".vjs-hls-quality-selector, .vjs-quality-selector"
                );
                const pipBtn = controlBarEl.querySelector(
                  ".vjs-picture-in-picture-control"
                );
                const fsBtn = controlBarEl.querySelector(
                  ".vjs-fullscreen-control"
                );
                const anchor = pipBtn || fsBtn;
                if (qualityBtn && anchor && anchor.parentNode) {
                  anchor.parentNode.insertBefore(qualityBtn, anchor);
                }
              }
            } catch (reorderErr) {
              console.warn(
                "[VideoPlayer] Quality button reorder failed:",
                reorderErr
              );
            }
          } else {
            console.log("[VideoPlayer] hlsQualitySelector not available");
          }
        } catch (err) {
          console.error(
            "[VideoPlayer] Error initializing quality selector:",
            err
          );
        }
      }

      // 모바일 터치/클릭 컨트롤: 컨트롤 바 토글
      let controlsTimeout;
      const isMobile =
        /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
        window.innerWidth <= 768;
      const isTablet = window.innerWidth > 768 && window.innerWidth <= 1024;
      const isPortrait = window.innerHeight > window.innerWidth; // 세로 모드 확인

      if (isMobile) {
        // 모바일에서 상단 오버레이 적용 (세로/가로 모두)
        // tech/DOM이 준비된 후에만 진행
        try {
          mobilePlayerEl = player.el();
          const controlBar =
            player.controlBar && player.controlBar.el
              ? player.controlBar.el()
              : null;
          if (!mobilePlayerEl || !controlBar) {
            console.warn("[VideoPlayer] Mobile controls: elements not ready");
          } else {
            // 초기에는 컨트롤 바 표시
            try {
              player.userActive(true);
            } catch (_) {}

            // 컨트롤 바 토글 함수
            const toggleControls = (e) => {
              if (controlBar.contains(e.target)) {
                return;
              }
              const isActive = player.userActive();
              if (isActive) {
                try {
                  player.userActive(false);
                } catch (_) {}
                if (controlsTimeout) clearTimeout(controlsTimeout);
              } else {
                try {
                  player.userActive(true);
                } catch (_) {}
                if (controlsTimeout) clearTimeout(controlsTimeout);
                if (!player.paused()) {
                  controlsTimeout = setTimeout(() => {
                    try {
                      player.userActive(false);
                    } catch (_) {}
                  }, 2000);
                }
              }
            };

            mobileOnTouchStart = (e) => {
              if (!controlBar.contains(e.target)) toggleControls(e);
            };
            mobilePlayerEl.addEventListener("touchstart", mobileOnTouchStart, {
              passive: true,
            });
            mobileOnPointerDown = (e) => {
              if (!controlBar.contains(e.target)) toggleControls(e);
            };
            mobilePlayerEl.addEventListener(
              "pointerdown",
              mobileOnPointerDown,
              { passive: true }
            );
            mobileOnClick = (e) => {
              if (!controlBar.contains(e.target)) toggleControls(e);
            };
            mobilePlayerEl.addEventListener("click", mobileOnClick);

            player.on("play", () => {
              try {
                player.userActive(true);
              } catch (_) {}
              if (controlsTimeout) clearTimeout(controlsTimeout);
              controlsTimeout = setTimeout(() => {
                try {
                  player.userActive(false);
                } catch (_) {}
              }, 2000);
            });

            player.on("pause", () => {
              if (controlsTimeout) clearTimeout(controlsTimeout);
              try {
                player.userActive(true);
              } catch (_) {}
            });

            // 상단 오버레이 컨트롤 컨테이너 생성 및 통합 설정 버튼 생성 (모바일 세로 전용)
            let topControls = mobilePlayerEl.querySelector(".vjs-top-controls");
            if (!topControls) {
              topControls = document.createElement("div");
              topControls.className = "vjs-top-controls";
              mobilePlayerEl.appendChild(topControls);
            }

            // 개별 버튼들을 상단으로 이동
            const moveButtonToTop = (selector, buttonText, iconClass) => {};

            // 해상도 버튼이 있으면 상단으로 이동
            const hasQualityButton = moveButtonToTop(
              ".vjs-hls-quality-selector, .vjs-quality-selector",
              "해상도",
              "vjs-icon-cog"
            );

            // 자막 버튼이 있으면 상단으로 이동
            const hasSubtitleButton = moveButtonToTop(
              ".vjs-subs-caps-button",
              "자막",
              "vjs-icon-captions"
            );

            // MutationObserver로 버튼이 추가될 때마다 상단으로 이동
            let mobileMutationTimeout = null;
            mobileObserver = new MutationObserver(() => {
              if (mobileMutationTimeout) return; // 이미 예약된 실행이 있으면 리턴

              mobileMutationTimeout = setTimeout(() => {
                // 새로 추가된 버튼들을 상단으로 이동
                moveButtonToTop(
                  ".vjs-hls-quality-selector, .vjs-quality-selector",
                  "해상도",
                  "vjs-icon-cog"
                );
                moveButtonToTop(
                  ".vjs-subs-caps-button",
                  "자막",
                  "vjs-icon-captions"
                );
                mobileMutationTimeout = null;
              }, 100); // 100ms throttle
            });
            mobileObserver.observe(controlBar, {
              childList: true,
              subtree: true,
            });

            // 즉시 이동 시도
            setTimeout(() => {
              moveButtonToTop(
                ".vjs-hls-quality-selector, .vjs-quality-selector",
                "해상도",
                "vjs-icon-cog"
              );
              moveButtonToTop(
                ".vjs-subs-caps-button",
                "자막",
                "vjs-icon-captions"
              );
            }, 100);

            // 모바일 세로 모드에서 전체화면 버튼 클릭 시 시뮬레이트 전체화면 토글
            const fullscreenButton = controlBar.querySelector(
              ".vjs-fullscreen-control"
            );
            if (fullscreenButton) {
              fullscreenButton.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (
                  player.el().classList.contains("vjs-simulated-fullscreen")
                ) {
                  player.el().classList.remove("vjs-simulated-fullscreen");
                  player.isFullscreen(false); // Video.js 내부 상태 업데이트
                  if (onFullscreenChange) onFullscreenChange(false);
                } else {
                  player.el().classList.add("vjs-simulated-fullscreen");
                  player.isFullscreen(true); // Video.js 내부 상태 업데이트
                  if (onFullscreenChange) onFullscreenChange(true);
                }
              };
            }
          }
        } catch (mobileErr) {
          console.warn("[VideoPlayer] Mobile controls init error:", mobileErr);
        }
      } else {
        // 데스크톱 및 태블릿만
        // 품질 버튼을 PiP 버튼 왼쪽으로 이동 (PiP 없으면 전체화면 왼쪽)
        let isMovingButton = false; // 중복 실행 방지 플래그

        const moveQualityButton = () => {
          if (isMovingButton) return; // 이미 실행 중이면 리턴

          try {
            const controlBarEl =
              player.controlBar && player.controlBar.el
                ? player.controlBar.el()
                : null;
            if (controlBarEl) {
              const qualityBtn = controlBarEl.querySelector(
                ".vjs-hls-quality-selector, .vjs-quality-selector"
              );
              const pipBtn = controlBarEl.querySelector(
                ".vjs-picture-in-picture-control"
              );
              const fullscreenBtn = controlBarEl.querySelector(
                ".vjs-fullscreen-control"
              );

              if (qualityBtn) {
                // 이미 올바른 위치에 있는지 확인
                const isBeforePip = pipBtn && qualityBtn.nextSibling === pipBtn;
                const isBeforeFullscreen =
                  !pipBtn &&
                  fullscreenBtn &&
                  qualityBtn.nextSibling === fullscreenBtn;

                if (isBeforePip || isBeforeFullscreen) {
                  return; // 이미 올바른 위치에 있으면 리턴
                }

                isMovingButton = true; // 이동 시작 플래그 설정

                // PiP 버튼이 있으면 PiP 왼쪽으로, 없으면 전체화면 왼쪽으로 이동
                if (pipBtn && pipBtn.parentNode) {
                  pipBtn.parentNode.insertBefore(qualityBtn, pipBtn);
                  console.log("[VideoPlayer] Moved quality button before PiP");
                } else if (fullscreenBtn && fullscreenBtn.parentNode) {
                  fullscreenBtn.parentNode.insertBefore(
                    qualityBtn,
                    fullscreenBtn
                  );
                  console.log(
                    "[VideoPlayer] Moved quality button before Fullscreen"
                  );
                }

                // 이동 완료 후 플래그 해제 (다음 프레임에서)
                setTimeout(() => {
                  isMovingButton = false;
                }, 0);
              }
            }
          } catch (reorderErr) {
            console.warn(
              "[VideoPlayer] Failed to reorder quality button:",
              reorderErr
            );
            isMovingButton = false; // 에러 시에도 플래그 해제
          }
        };

        // 즉시 이동 시도
        moveQualityButton();

        // MutationObserver로 버튼이 추가될 때마다 이동 (한 번만 실행)
        let hasMovedButton = false;
        desktopObserver = new MutationObserver((mutations) => {
          if (hasMovedButton) return; // 이미 이동했으면 리턴

          // 품질 버튼이 추가되었는지 확인
          const hasQualityButton = mutations.some((mutation) =>
            Array.from(mutation.addedNodes).some(
              (node) =>
                node.nodeType === 1 &&
                (node.classList?.contains("vjs-hls-quality-selector") ||
                  node.classList?.contains("vjs-quality-selector") ||
                  node.querySelector?.(
                    ".vjs-hls-quality-selector, .vjs-quality-selector"
                  ))
            )
          );

          if (hasQualityButton) {
            hasMovedButton = true;
            setTimeout(() => {
              moveQualityButton();
            }, 50);
          }
        });
        desktopObserver.observe(player.controlBar.el(), {
          childList: true,
          subtree: true,
        });
      }
    });

    // 이벤트: 메타데이터 로드 완료
    player.on("loadedmetadata", () => {
      console.log("[VideoPlayer] Metadata loaded");
      const duration = player.duration();
      if (onDuration) {
        onDuration(duration);
      }
    });

    // 이벤트: 재생 가능 - 에러 방지
    try {
      player.one("loadedmetadata", () => {
        console.log(
          "[VideoPlayer] Metadata loaded, duration:",
          player.duration()
        );
      });
    } catch (err) {
      console.error("[VideoPlayer] Error in loadedmetadata listener:", err);
    }

    try {
      player.one("canplay", () => {
        console.log(
          "[VideoPlayer] Can play, startTime:",
          startTime,
          "autoplay:",
          autoplay
        );

        if (onReady) {
          onReady(player);
        }

        // 시작 시간 설정
        if (startTime > 0) {
          console.log("[VideoPlayer] Setting start time to:", startTime);
          setTimeout(() => {
            player.currentTime(startTime);
            console.log(
              "[VideoPlayer] Current time set to:",
              player.currentTime()
            );

            // autoplay가 true면 재생 시작
            if (autoplay) {
              setTimeout(() => {
                player
                  .play()
                  .catch((err) =>
                    console.log("[VideoPlayer] Autoplay prevented:", err)
                  );
              }, 100);
            }
          }, 300);
        } else if (autoplay) {
          // startTime이 0이고 autoplay가 true면 바로 재생
          setTimeout(() => {
            player
              .play()
              .catch((err) =>
                console.log("[VideoPlayer] Autoplay prevented:", err)
              );
          }, 300);
        }
      });
    } catch (err) {
      console.error("[VideoPlayer] Error in canplay listener:", err);
    }

    // 이벤트: 시간 업데이트 (진행률 추적 및 탐색 제한)
    let lastProgressTime = 0;
    let maxWatchedTime = startTime || 0;

    player.on("timeupdate", () => {
      const currentTime = player.currentTime();

      // 시청한 최대 시간 업데이트
      if (currentTime > maxWatchedTime) {
        maxWatchedTime = currentTime;
      }

      // 1초마다 한 번씩만 onProgress 호출
      if (onProgress && currentTime - lastProgressTime >= 1) {
        onProgress(currentTime);
        lastProgressTime = currentTime;
      }
    });

    // 진행바 클릭 조건부 활성화 (완료된 콘텐츠에서만 탐색 허용)
    const progressControl = player.controlBar.progressControl;
    if (progressControl) {
      if (isCompleted) {
        // 완료된 콘텐츠: 탐색 허용
        progressControl.enable();
        progressControl.el().style.pointerEvents = "auto";
        progressControl.el().style.cursor = "pointer";

        const seekBar = progressControl.getChild("seekBar");
        if (seekBar) {
          seekBar.el().style.pointerEvents = "auto";
          seekBar.el().style.cursor = "pointer";
        }

        console.log("[VideoPlayer] Progress bar enabled for completed content");

        // 진행바 터치/클릭 시 시간 표시 기능 추가
        const progressEl = progressControl.el();
        const seekBarEl = seekBar ? seekBar.el() : null;

        // 시간 표시 요소 생성
        const timeDisplay = document.createElement("div");
        timeDisplay.className = "vjs-progress-time-display";
        progressEl.appendChild(timeDisplay);

        // 시간 포맷 함수
        const formatTime = (seconds) => {
          const mins = Math.floor(seconds / 60);
          const secs = Math.floor(seconds % 60);
          return `${mins}:${secs.toString().padStart(2, "0")}`;
        };

        // 마우스/터치 이벤트 핸들러
        const showTimeAtPosition = (e) => {
          if (!seekBarEl) return;

          const rect = seekBarEl.getBoundingClientRect();
          const clientX = e.type.includes("touch")
            ? e.touches[0].clientX
            : e.clientX;
          const x = clientX - rect.left;
          const percentage = Math.max(0, Math.min(1, x / rect.width));
          const time = percentage * player.duration();

          timeDisplay.textContent = formatTime(time);
          timeDisplay.style.left = `${percentage * 100}%`;
          timeDisplay.classList.add("show");
        };

        const hideTimeDisplay = () => {
          timeDisplay.classList.remove("show");
        };

        // 마우스 이벤트 (데스크톱)
        seekBarEl.addEventListener("mousemove", showTimeAtPosition);
        seekBarEl.addEventListener("mouseleave", hideTimeDisplay);

        // 터치 이벤트 (모바일)
        seekBarEl.addEventListener("touchmove", showTimeAtPosition, {
          passive: true,
        });
        seekBarEl.addEventListener("touchend", hideTimeDisplay);
        seekBarEl.addEventListener("touchcancel", hideTimeDisplay);

        // 클릭/터치로 탐색
        const seekToPosition = (e) => {
          if (!seekBarEl) return;

          const rect = seekBarEl.getBoundingClientRect();
          const clientX = e.type.includes("touch")
            ? e.changedTouches[0].clientX
            : e.clientX;
          const x = clientX - rect.left;
          const percentage = Math.max(0, Math.min(1, x / rect.width));
          const time = percentage * player.duration();

          player.currentTime(time);
          hideTimeDisplay();
        };

        seekBarEl.addEventListener("click", seekToPosition);
        seekBarEl.addEventListener("touchend", (e) => {
          // 터치 종료 시 탐색 (단, touchmove가 발생하지 않은 경우만)
          if (!e.target.classList.contains("vjs-progress-time-display")) {
            seekToPosition(e);
          }
        });
      } else {
        // 미완료 콘텐츠: 탐색 차단
        progressControl.disable();
        progressControl.el().style.pointerEvents = "none";
        progressControl.el().style.cursor = "default";

        const seekBar = progressControl.getChild("seekBar");
        if (seekBar) {
          seekBar.el().style.pointerEvents = "none";
          seekBar.el().style.cursor = "default";
        }

        console.log(
          "[VideoPlayer] Progress bar disabled for incomplete content"
        );
      }
    }

    // 이벤트: 재생 종료
    player.on("ended", () => {
      console.log("[VideoPlayer] Video ended");
      if (onEnded) {
        onEnded();
      }
    });

    // 페이지 가시성 API를 사용하여 백그라운드 재생 보호를 위한 변수
    // pause 핸들러에서도 접근할 수 있도록 먼저 선언
    let savedCurrentTime = null;
    let wasPlayingBeforeHidden = false;
    let visibilityRestoreTimeout = null;

    // 이벤트: 재생 시작
    player.on("play", () => {
      const isFullscreen = player.isFullscreen();
      console.log("[VideoPlayer] Video play, fullscreen:", isFullscreen);
      if (onPlay) {
        onPlay(isFullscreen);
      }
    });

    // 이벤트: 일시정지
    player.on("pause", () => {
      const isFullscreen = player.isFullscreen();
      console.log("[VideoPlayer] Video pause, fullscreen:", isFullscreen);
      
      // 탭이 숨겨진 상태에서 pause 이벤트가 발생하면 (브라우저가 자동으로 정지시킨 경우)
      // 저장된 시간이 있고 재생 중이었다면 자동으로 재생 재개 시도
      if (document.hidden && savedCurrentTime !== null && wasPlayingBeforeHidden) {
        const currentTime = player.currentTime();
        // 시간이 저장된 시간보다 많이 줄어들었다면 (0:00으로 돌아간 경우)
        if (currentTime < savedCurrentTime - 2) {
          console.log("[VideoPlayer] 탭 전환으로 인한 정지 감지 - 시간 복원 및 재생 재개");
          setTimeout(() => {
            player.currentTime(savedCurrentTime);
            player.play().catch((err) => {
              console.log("[VideoPlayer] 자동 재생 실패:", err);
            });
          }, 100);
        }
      }
      
      if (onPause) {
        onPause(isFullscreen);
      }
    });

    // 이벤트: 전체화면 변경
    player.on("fullscreenchange", () => {
      const isFullscreen = player.isFullscreen();
      console.log("[VideoPlayer] Fullscreen changed:", isFullscreen);
      if (onFullscreenChange) {
        onFullscreenChange(isFullscreen);
      }
    });

    // 자막 추가
    if (subtitles && subtitles.length > 0) {
      console.log("[VideoPlayer] Adding subtitles:", subtitles.length);
      subtitles.forEach((subtitle, index) => {
        player.addRemoteTextTrack(
          {
            kind: subtitle.kind || "subtitles",
            src: subtitle.src,
            srclang: subtitle.srcLang,
            label: subtitle.label,
            default: subtitle.default || false,
          },
          false
        );
      });
    }

    // 키보드 단축키
    const handleKeyPress = (e) => {
      // input이나 textarea에서는 동작하지 않도록
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") {
        return;
      }

      if (!player) return;

      switch (e.key) {
        case " ":
        case "k":
          e.preventDefault();
          if (player.paused()) {
            player.play();
          } else {
            player.pause();
          }
          break;
        case "ArrowLeft":
          // 키보드 뒤로가기 완전 차단
          e.preventDefault();
          break;
        case "ArrowRight":
          // 앞으로 건너뛰기 비활성화
          e.preventDefault();
          break;
        case "f":
          e.preventDefault();
          if (player.isFullscreen()) {
            player.exitFullscreen();
          } else {
            player.requestFullscreen();
          }
          break;
        case "m":
          e.preventDefault();
          player.muted(!player.muted());
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyPress);

    // 페이지 가시성 API를 사용하여 백그라운드 재생 보호
    const handleVisibilityChange = () => {
      if (!player || !playerRef.current) return;
      
      if (document.hidden) {
        // 탭이 숨겨질 때: 현재 재생 상태와 시간 저장
        savedCurrentTime = player.currentTime();
        wasPlayingBeforeHidden = !player.paused();
        console.log("[VideoPlayer] 탭 숨김 감지 - 재생 상태 저장:", {
          currentTime: savedCurrentTime,
          wasPlaying: wasPlayingBeforeHidden
        });
        
        // 기존 복원 타이머가 있으면 취소
        if (visibilityRestoreTimeout) {
          clearTimeout(visibilityRestoreTimeout);
          visibilityRestoreTimeout = null;
        }
      } else {
        // 탭이 다시 보일 때: 저장된 시간으로 복원 (0:00으로 돌아간 경우)
        if (savedCurrentTime !== null) {
          const currentTime = player.currentTime();
          // 시간이 저장된 시간보다 많이 줄어들었다면 (0:00으로 돌아간 경우) 복원
          if (currentTime < savedCurrentTime - 1) {
            console.log("[VideoPlayer] 탭 복귀 감지 - 시간 복원:", savedCurrentTime, "현재 시간:", currentTime);
            
            // 짧은 지연 후 시간 복원 (비디오가 완전히 로드될 때까지 대기)
            visibilityRestoreTimeout = setTimeout(() => {
              player.currentTime(savedCurrentTime);
              
              // 이전에 재생 중이었다면 재생 재개
              if (wasPlayingBeforeHidden && player.paused()) {
                setTimeout(() => {
                  player.play().catch((err) => {
                    console.log("[VideoPlayer] 복원 후 재생 실패:", err);
                  });
                }, 100);
              }
              savedCurrentTime = null;
              wasPlayingBeforeHidden = false;
            }, 200);
          } else {
            // 시간이 정상적으로 유지되고 있으면 저장값 초기화
            savedCurrentTime = null;
            wasPlayingBeforeHidden = false;
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // 클린업 함수
    return () => {
      console.log("[VideoPlayer] Cleanup");
      document.removeEventListener("keydown", handleKeyPress);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      
      // 타이머 정리
      if (visibilityRestoreTimeout) {
        clearTimeout(visibilityRestoreTimeout);
        visibilityRestoreTimeout = null;
      }

      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
      // 모바일 리스너 정리 (존재할 때만 제거)
      try {
        if (mobilePlayerEl && mobileOnTouchStart)
          mobilePlayerEl.removeEventListener("touchstart", mobileOnTouchStart);
        if (mobilePlayerEl && mobileOnPointerDown)
          mobilePlayerEl.removeEventListener(
            "pointerdown",
            mobileOnPointerDown
          );
        if (mobilePlayerEl && mobileOnClick)
          mobilePlayerEl.removeEventListener("click", mobileOnClick);
        if (handleDocumentClick)
          document.removeEventListener("click", handleDocumentClick);
        if (mobileObserver) mobileObserver.disconnect();
        if (desktopObserver) desktopObserver.disconnect();
        if (mobileMutationTimeout) clearTimeout(mobileMutationTimeout);
        if (mutationTimeout) clearTimeout(mutationTimeout);
      } catch (_) {}
    };
  }, [src]); // src가 변경될 때만 재생성

  return (
    <div className={`video-player-container ${className}`} style={style}>
      <div data-vjs-player>
        <video
          ref={videoRef}
          className="video-js vjs-big-play-centered"
          playsInline
          webkit-playsinline="true"
        />
      </div>
    </div>
  );
};

export default VideoPlayer;
